import React, { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdOutlineEdit } from "react-icons/md";
import { getUserByID, updateUserInfo } from "./services/myprofile.services";
import { useFormik } from "formik";
import moment from "moment";
import Loading from "../../components/spinner/Loading";
import * as Yup from "yup";
import { getLocalItem } from "../../_helpers/storageUtils";
import { handleUppercaseChange } from "../../_helpers/formatUtils";
import { Country, State, City } from "country-state-city";
import i18nNationality from "i18n-nationality";
import en from "i18n-nationality/langs/en.json";
i18nNationality.registerLocale(en);

const ProfileCard = () => {
  const userData = useAppSelector((state) => state.profile.userData);
  // const NationalityList = useAppSelector(
  //   (state) => state.nationality.NationalityList
  // );
  // const CityList = useAppSelector((state) => state.city.CityList);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.profile.loading);
  const closeBtnRef = useRef<any>(null);
  const nationality_list = i18nNationality.getNames("en");

  useEffect(() => {
    dispatch(getUserByID({ user_guid: getLocalItem("user_guid") }));
  }, []);

  const validationSchema = Yup.object({
    first_name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
      .min(2, "Must be at least 2 characters")
      .max(30, "Must be at most 30 characters")
      .required("First name is required"),

    last_name: Yup.string()
      .matches(/^[A-Za-z\s]+$/, "Only alphabets are allowed")
      .min(2, "Must be at least 2 characters")
      .max(30, "Must be at most 30 characters")
      .required("Last name is required"),

    date_of_birth: Yup.date()
      .max(new Date(), "Date of birth cannot be in the future")
      .required("Date of birth is required"),

    gender: Yup.string()
      .oneOf(["1", "2"], "Select a valid gender")
      .required("Gender is required"),
  });

  const formik = useFormik({
    initialValues: {
      first_name: userData?.first_name || "",
      last_name: userData?.last_name || "",
      date_of_birth: userData?.date_of_birth
        ? moment(userData?.date_of_birth, "DD/MM/YYYY").format("YYYY-MM-DD")
        : "",
      gender: userData?.gender || "",
      country_code: userData?.country_code || "",
      state_code: userData?.state_code || "",
      country_name: userData?.country_name || "",
      state_name: userData?.state_name || "",
      city_name: userData?.city_name || "",
      nationality_code: userData?.nationality_code || "",
      nationality_name: userData?.nationality_name || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      const payload = {
        state_code: values?.state_code ? values?.state_code : "",
        country_code: values?.country_code ? values?.country_code : "",
        city_name: values?.city_name ? values?.city_name : "",
        state_name: values?.state_name ? values?.state_name : "",
        country_name: values?.country_name ? values?.country_name : "",
        nationality_code: values?.nationality_code
          ? values?.nationality_code
          : "",
        nationality_name: values?.nationality_name
          ? values?.nationality_name
          : "",
        gender: values?.gender ? values?.gender : "",
        first_name: values?.first_name ? values?.first_name : "",
        last_name: values?.last_name ? values?.last_name : "",
        date_of_birth: values?.date_of_birth
          ? moment(values?.date_of_birth)?.format("YYYY-MM-DD")
          : "",
        user_guid: getLocalItem("user_guid"),
        isactive: true,
      };
      const response = await dispatch(updateUserInfo(payload));
      if (response.meta.requestStatus === "fulfilled") {
        setSubmitting(false);
        resetForm();
        if (closeBtnRef.current) {
          closeBtnRef.current.click();
        }
        dispatch(getUserByID({ user_guid: getLocalItem("user_guid") }));
      }
    },
    enableReinitialize: true,
  });

  return (
    <div className="position-relative">
      <Loading loading={loading} />
      <div className="profile-heading">
        <div className="prfile-name">
          <h2>Profile</h2>
          <p>Basic info, for a faster booking experience</p>
        </div>
        <form className="add-travel">
          <a
            className="travel-btn"
            data-bs-toggle="modal"
            data-bs-target="#profileform"
            href="javascript:void(0)"
          >
            <MdOutlineEdit /> Edit
          </a>
          <div
            className="modal fade"
            id="profileform"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex={-1}
            aria-labelledby="edit-profile"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h4 className="modal-title fs-5" id="edit-profile">
                    Edit Profile
                  </h4>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                    onClick={() => formik.resetForm()}
                  ></button>
                </div>
                <div className="modal-body travel-form-detail">
                  <div className="travel-form">
                    <form>
                      <div className="field-set">
                        <div className="field">
                          <label
                            className={
                              formik.touched.first_name &&
                              formik.errors.first_name
                                ? "error-label"
                                : ""
                            }
                          >
                            First Name <code>*</code>
                          </label>
                          <input
                            type="text"
                            name="first_name"
                            value={formik.values.first_name}
                            onChange={(e) => handleUppercaseChange(formik, e)}
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.first_name &&
                              formik.errors.first_name
                                ? "error-border"
                                : ""
                            }
                          />
                          {formik.touched.first_name &&
                          formik.errors.first_name &&
                          typeof formik.errors.first_name === "string" ? (
                            <div className="error-label">
                              {formik.errors.first_name}
                            </div>
                          ) : null}
                        </div>
                        <div className="field">
                          <label
                            className={
                              formik.touched.last_name &&
                              formik.errors.last_name
                                ? "error-label"
                                : ""
                            }
                          >
                            Last Name <code>*</code>
                          </label>
                          <input
                            type="text"
                            name="last_name"
                            value={formik.values.last_name}
                            onChange={(e) => handleUppercaseChange(formik, e)}
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.last_name &&
                              formik.errors.last_name
                                ? "error-border"
                                : ""
                            }
                          />
                          {formik.touched.last_name &&
                          formik.errors.last_name &&
                          typeof formik.errors.last_name === "string" ? (
                            <div className="error-label">
                              {formik.errors.last_name}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="field-set">
                        <div className="field">
                          <label
                            className={
                              formik.touched.date_of_birth &&
                              formik.errors.date_of_birth
                                ? "error-label"
                                : ""
                            }
                          >
                            Birthday <code>*</code>
                          </label>
                          <input
                            type="date"
                            name="date_of_birth"
                            max={moment().format("YYYY-MM-DD")}
                            value={formik.values.date_of_birth}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.date_of_birth &&
                              formik.errors.date_of_birth
                                ? "error-border"
                                : ""
                            }
                          />
                          {formik.touched.date_of_birth &&
                          formik.errors.date_of_birth &&
                          typeof formik.errors.date_of_birth === "string" ? (
                            <div className="error-label">
                              {formik.errors.date_of_birth}
                            </div>
                          ) : null}
                        </div>
                        <div className="field">
                          <label
                            className={
                              formik.touched.gender && formik.errors.gender
                                ? "error-label"
                                : ""
                            }
                          >
                            Gender <code>*</code>
                          </label>
                          <select
                            name="gender"
                            value={formik.values.gender}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            className={
                              formik.touched.gender && formik.errors.gender
                                ? "error-border"
                                : ""
                            }
                          >
                            <option value="">Select Gender</option>
                            <option value={1}>Male</option>
                            <option value={2}>Female</option>
                          </select>
                          {formik.touched.gender &&
                          formik.errors.gender &&
                          formik.errors.gender &&
                          typeof formik.errors.gender === "string" ? (
                            <div className="error-label">
                              {formik.errors.gender}
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div className="field-set">
                        <div className="field">
                          <label>Nationality</label>
                          <select
                            name="nationality_code"
                            value={formik.values.nationality_code}
                            onChange={(e) => {
                              const code = e?.target?.value;
                              const name = nationality_list[code] || "";
                              formik.setFieldValue("nationality_code", code);
                              formik.setFieldValue("nationality_name", name);
                              formik.setFieldValue("country_code", code);
                              const selectedCountry =
                                Country.getAllCountries().find(
                                  (country) => country?.isoCode === code
                                );
                              const country_name = selectedCountry
                                ? selectedCountry?.name
                                : "";
                              formik.setFieldValue(
                                "country_name",
                                country_name
                              );
                            }}
                            // onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select Nationality</option>
                            {(Object.keys(nationality_list) || [])?.map(
                              (code: any) => (
                                <option key={code} value={code}>
                                  {nationality_list[code]}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                        <div className="field">
                          <label>Country</label>
                          <select
                            name="country_code"
                            value={formik.values.country_code}
                            onChange={(e) => {
                              const code = e?.target?.value;
                              const selectedCountry =
                                Country.getAllCountries().find(
                                  (country) => country?.isoCode === code
                                );
                              const name = selectedCountry
                                ? selectedCountry?.name
                                : "";
                              formik.setFieldValue("country_code", code);
                              formik.setFieldValue("country_name", name);
                              const nationality_name =
                                nationality_list[code] || "";
                              formik.setFieldValue("nationality_code", code);
                              formik.setFieldValue(
                                "nationality_name",
                                nationality_name
                              );
                            }}
                            // onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select Country</option>
                            {Country.getAllCountries()?.map((e: any) => (
                              <option key={e?.isoCode} value={e?.isoCode}>
                                {e?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="field-set">
                        <div className="field">
                          <label>State</label>
                          <select
                            name="state_code"
                            value={formik.values.state_code}
                            onChange={(e) => {
                              const code = e?.target?.value;
                              const selectedState = State.getStatesOfCountry(
                                formik.values.country_code
                              ).find((state) => state?.isoCode === code);
                              const name = selectedState
                                ? selectedState?.name
                                : "";
                              formik.setFieldValue("state_code", code);
                              formik.setFieldValue("state_name", name);
                            }}
                            // onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select State</option>
                            {State?.getStatesOfCountry(
                              formik.values.country_code
                            )?.map((e: any) => (
                              <option key={e?.isoCode} value={e?.isoCode}>
                                {e?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="field">
                          <label>City</label>
                          <select
                            name="city_name"
                            value={formik.values.city_name}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          >
                            <option value="">Select City</option>
                            {City.getCitiesOfState(
                              formik.values.country_code,
                              formik.values.state_code
                            )?.map((e) => (
                              <option key={e?.name} value={e?.name}>
                                {e?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </form>
                  </div>
                  <div className="travel-detail-btns mt-2">
                    <a
                      className="disable"
                      href="javascript:void(0)"
                      data-bs-dismiss="modal"
                      ref={closeBtnRef}
                      onClick={() => formik.resetForm()}
                    >
                      Cancel
                    </a>
                    <a
                      href="javascript:void(0)"
                      onClick={() => formik.handleSubmit()}
                    >
                      Save
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <table>
        <tbody>
          <tr>
            <td className="w-40">First Name</td>
            <td>
              {userData?.first_name ? (
                <span>{userData?.first_name || ""}</span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td>Last Name</td>
            <td>
              {userData?.last_name ? (
                <span>{userData?.last_name || ""}</span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td>Date of Birth</td>
            <td>
              {userData?.date_of_birth ? (
                <span>{userData?.date_of_birth}</span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td>Gender</td>
            <td>
              {userData?.gender ? (
                <span>
                  {userData?.gender === 1
                    ? "Male"
                    : userData?.gender === 2
                    ? "Female"
                    : ""}
                </span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td>Nationality</td>
            <td>
              {userData?.nationality_name ? (
                <span>{userData?.nationality_name}</span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td>Country</td>
            <td>
              {userData?.country_name ? (
                <span>{userData?.country_name}</span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td>State</td>
            <td>
              {userData?.state_name ? (
                <span>{userData?.state_name}</span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
          <tr>
            <td>City</td>
            <td>
              {userData?.city_name ? (
                <span>{userData?.city_name}</span>
              ) : (
                <span
                  className="profile-add-span"
                  data-bs-toggle="modal"
                  data-bs-target="#profileform"
                >
                  + Add
                </span>
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default ProfileCard;
